import * as XLSX from "xlsx";

// ✅ Same "Report Mode" filter contract used by DcrSchoolPDF.js:
// - ["All"] / empty            => everything included ("All in One").
// - Payment mode names         => only those mode-wise collection sheets
//                                  (+ Total Collection Summary) are built.
// - "Admission Form Fee" / "Misc Fee" => independent on/off toggles.
//
// sheetLayout controls how the Fee Collection rows are split across sheets
// in the SAME workbook:
// - "single"  => "All in 1 Sheet"            (one "Fee Collection" sheet)
// - "monthly" => "Monthly Separate Sheet"    (one sheet per receipt month)
// - "date"    => "Date Separate Sheet"       (one sheet per receipt date)
//
// Admission Form Fee / Misc Fee always get their own dedicated sheet(s)
// (splitting those by date/month isn't meaningful), plus a combined
// Summary sheet at the end — mirroring the last page of the PDF report.

const FEE_SHEET_HEADER = [
  "SL No.",
  "Receipt Date",
  "Receipt No.",
  "Admission No.",
  "Student Name",
  "Class & Section",
  "Development Charges",
  "Admission Fee",
  "Months Paid",
  "Monthly Fee",
  "Transport Fee",
  "Total Amount",
  "Mode of Payment",
  "Order ID",
  "Collected By",
  "Txn. Details",
];

// Excel sheet names: max 31 chars, no \ / ? * [ ] characters, must be unique.
const sanitizeSheetName = (name, usedNames) => {
  let sheetName = String(name || "Sheet").replace(/[\\/*?:[\]]/g, "-");
  if (sheetName.length > 31) sheetName = sheetName.substring(0, 31);
  if (!sheetName) sheetName = "Sheet";

  let finalName = sheetName;
  let suffix = 1;
  while (usedNames.has(finalName)) {
    const trimmed = sheetName.substring(0, 28);
    finalName = `${trimmed} (${suffix})`;
    suffix += 1;
  }
  usedNames.add(finalName);
  return finalName;
};

const addFeeSheet = (workbook, usedNames, sheetTitle, rows) => {
  const body = rows.map((row, i) => [
    i + 1,
    row.receiptDate,
    row.receipt_no,
    row.admission_no,
    row.student_name,
    row.classSection,
    row.developmentCharges,
    row.admissionFee,
    row.monthsPaid,
    row.monthlyFee,
    row.transportFee,
    row.totalAmount,
    row.mode,
    row.orderId,
    row.collectedBy,
    row.txnDetails,
  ]);

  const totals = rows.reduce(
    (acc, row) => {
      acc.development += row.developmentCharges || 0;
      acc.admission += row.admissionFee || 0;
      acc.monthly += row.monthlyFee || 0;
      acc.transport += row.transportFee || 0;
      acc.total += row.totalAmount || 0;
      return acc;
    },
    { development: 0, admission: 0, monthly: 0, transport: 0, total: 0 },
  );

  const totalRow = [
    "Total",
    "",
    "",
    "",
    "",
    "",
    totals.development,
    totals.admission,
    "",
    totals.monthly,
    totals.transport,
    totals.total,
    "",
    "",
    "",
    "",
  ];

  const sheetData = rows.length
    ? [FEE_SHEET_HEADER, ...body, totalRow]
    : [FEE_SHEET_HEADER, ["No Records Found"]];

  const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
  XLSX.utils.book_append_sheet(
    workbook,
    worksheet,
    sanitizeSheetName(sheetTitle, usedNames),
  );
};

const DcrSchoolExcel = (
  feeData,
  admissionFormData = [],
  miscellaneousFeesData = [],
  selectedModes = [],
  sheetLayout = "single",
) => {
  if (!feeData || !Array.isArray(feeData) || feeData.length === 0) {
    alert("No data available to export.");
    return;
  }

  // ── Same mode/section filter logic as DcrSchoolPDF.js ─────────────────────
  const SECTION_KEYS = ["Admission Form Fee", "Misc Fee"];
  const allModesSelected =
    !selectedModes ||
    selectedModes.length === 0 ||
    selectedModes.includes("All");

  const selectedPaymentModes = allModesSelected
    ? []
    : selectedModes.filter((m) => !SECTION_KEYS.includes(m) && m !== "All");

  let hasPaymentModeSelection =
    allModesSelected || selectedPaymentModes.length > 0;
  const isModeAllowed = (mode) =>
    allModesSelected || selectedPaymentModes.includes(mode);

  const includeAdmissionForm =
    allModesSelected || selectedModes.includes("Admission Form Fee");
  const includeMiscFee = allModesSelected || selectedModes.includes("Misc Fee");

  let allFeesData = feeData.flatMap((entry) => entry.monthlyFees || []);

  let modeWiseRows = {};
  let modeWiseTotals = {};
  let modeHeadWiseTotals = {};
  let totalAdmissionFee = 0;
  let totalDevelopmentCharges = 0;
  let totalMonthlyFee = 0;
  let totalTransportFee = 0;

  allFeesData.forEach((student) => {
    const {
      admission_no,
      receipt_no,
      date,
      student_name,
      class: studentClass,
      currentSection,
      collectedBy,
      feeDetails,
    } = student;

    if (!feeDetails || !feeDetails.monthPaid) return;

    let studentTotalAmount = 0;
    let paymentModes = {};
    let detailsList = new Set();
    let orderIdsList = new Set();

    // ✅ order_id comes back on the Online payment mode's entry in the API
    // response, same object as amount/detail/bank. If your API uses a
    // different key (e.g. razorpay_order_id), adjust the destructure below.
    const addPayment = (fee, feeType) => {
      if (fee?.paid) {
        studentTotalAmount += fee.TotalAmt || 0;
        if (feeType === "admission") totalAdmissionFee += fee.TotalAmt || 0;
        if (feeType === "development")
          totalDevelopmentCharges += fee.TotalAmt || 0;

        Object.entries(fee.paymentMode || {}).forEach(
          ([mode, { amount, detail, bank, order_id }]) => {
            paymentModes[mode] = (paymentModes[mode] || 0) + amount;
            if (!modeHeadWiseTotals[mode]) {
              modeHeadWiseTotals[mode] = {
                admission: 0,
                development: 0,
                monthly: 0,
                transport: 0,
              };
            }
            modeHeadWiseTotals[mode][feeType] += amount;
            if (detail) detailsList.add(`Detail: ${detail}`);
            if (bank) detailsList.add(`Bank: ${bank}`);
            if (order_id) orderIdsList.add(order_id);
          },
        );
      }
    };

    addPayment(feeDetails.developmentCharges, "development");
    addPayment(feeDetails.admissionFee, "admission");

    let monthlyFeeTotal = 0;
    let transportFeeTotal = 0;
    let monthPaidList = feeDetails.monthPaid
      .filter((m) => m.paid)
      .map((m) => {
        monthlyFeeTotal += m.monthlyFee ?? 0;
        transportFeeTotal += m.transportFee ?? 0;
        totalMonthlyFee += m.monthlyFee ?? 0;
        totalTransportFee += m.transportFee ?? 0;
        studentTotalAmount += m.TotalAmt ?? 0;

        Object.entries(m.paymentMode || {}).forEach(
          ([mode, { amount, detail, bank }]) => {
            paymentModes[mode] = (paymentModes[mode] || 0) + amount;
            if (!modeHeadWiseTotals[mode]) {
              modeHeadWiseTotals[mode] = {
                admission: 0,
                development: 0,
                monthly: 0,
                transport: 0,
              };
            }
            modeHeadWiseTotals[mode].monthly += m.monthlyFee ?? 0;
            modeHeadWiseTotals[mode].transport += m.transportFee ?? 0;
            if (detail) detailsList.add(`Detail: ${detail}`);
            if (bank) detailsList.add(`Bank: ${bank}`);
          },
        );
        return m.month;
      })
      .join(", ");

    // ✅ Only single-payment-mode receipts go into the mode-wise sheets —
    // kept consistent with the existing DCR PDF report (its multi-mode
    // table is currently disabled there too).
    const modeKeys = Object.keys(paymentModes);
    if (modeKeys.length === 1) {
      const [mode] = modeKeys;
      const receiptDateObj = new Date(date);
      const receiptDate = receiptDateObj.toLocaleDateString("en-GB");
      const monthKey = `${receiptDateObj.toLocaleString("en-US", {
        month: "short",
      })} ${receiptDateObj.getFullYear()}`;

      if (!modeWiseRows[mode]) modeWiseRows[mode] = [];
      modeWiseRows[mode].push({
        receiptDateObj,
        receiptDate,
        monthKey,
        receipt_no,
        admission_no,
        student_name,
        classSection: `${studentClass}, ${currentSection}`,
        developmentCharges: feeDetails.developmentCharges?.TotalAmt || 0,
        admissionFee: feeDetails.admissionFee?.TotalAmt || 0,
        monthsPaid: monthPaidList,
        monthlyFee: monthlyFeeTotal,
        transportFee: transportFeeTotal,
        totalAmount: studentTotalAmount,
        collectedBy: collectedBy || "N/A",
        txnDetails: [...detailsList].join("; ") || "N/A",
        mode,
      });

      if (!modeWiseTotals[mode]) {
        modeWiseTotals[mode] = {
          development: 0,
          admission: 0,
          monthly: 0,
          transport: 0,
          paidAmount: 0,
        };
      }
      modeWiseTotals[mode].development +=
        feeDetails.developmentCharges?.TotalAmt || 0;
      modeWiseTotals[mode].admission +=
        feeDetails.admissionFee?.TotalAmt || 0;
      modeWiseTotals[mode].monthly += monthlyFeeTotal;
      modeWiseTotals[mode].transport += transportFeeTotal;
      modeWiseTotals[mode].paidAmount += paymentModes[mode];
    }
  });

  // ✅ Restrict down to the selected mode(s) — same behaviour as the PDF.
  if (!allModesSelected) {
    if (!hasPaymentModeSelection) {
      modeWiseRows = {};
      modeWiseTotals = {};
      modeHeadWiseTotals = {};
    } else {
      Object.keys(modeWiseRows).forEach((mode) => {
        if (!isModeAllowed(mode)) {
          delete modeWiseRows[mode];
          delete modeWiseTotals[mode];
          delete modeHeadWiseTotals[mode];
        }
      });

      if (Object.keys(modeWiseRows).length === 0) {
        hasPaymentModeSelection = false;
      }
    }
  }

  if (!hasPaymentModeSelection && !includeAdmissionForm && !includeMiscFee) {
    alert("No records found for the selected report mode(s).");
    return;
  }

  // ── Build workbook ────────────────────────────────────────────────────────
  const workbook = XLSX.utils.book_new();
  const usedSheetNames = new Set();

  if (hasPaymentModeSelection) {
    let allRows = [];
    Object.values(modeWiseRows).forEach((rows) => {
      allRows = allRows.concat(rows);
    });
    allRows.sort((a, b) => a.receiptDateObj - b.receiptDateObj);

    if (sheetLayout === "monthly") {
      const monthOrder = [];
      const monthGroups = {};
      allRows.forEach((row) => {
        if (!monthGroups[row.monthKey]) {
          monthGroups[row.monthKey] = [];
          monthOrder.push(row.monthKey);
        }
        monthGroups[row.monthKey].push(row);
      });
      monthOrder.sort(
        (a, b) => new Date(`1 ${a}`) - new Date(`1 ${b}`),
      );
      monthOrder.forEach((key) => {
        addFeeSheet(workbook, usedSheetNames, key, monthGroups[key]);
      });
    } else if (sheetLayout === "date") {
      const dateOrder = [];
      const dateGroups = {};
      allRows.forEach((row) => {
        if (!dateGroups[row.receiptDate]) {
          dateGroups[row.receiptDate] = [];
          dateOrder.push(row.receiptDate);
        }
        dateGroups[row.receiptDate].push(row);
      });
      dateOrder.sort((a, b) => {
        const [da, ma, ya] = a.split("/").map(Number);
        const [db, mb, yb] = b.split("/").map(Number);
        return new Date(ya, ma - 1, da) - new Date(yb, mb - 1, db);
      });
      dateOrder.forEach((key) => {
        addFeeSheet(
          workbook,
          usedSheetNames,
          key.split("/").join("-"),
          dateGroups[key],
        );
      });
    } else {
      // "single" — All in 1 Sheet
      addFeeSheet(workbook, usedSheetNames, "Fee Collection", allRows);
    }

    // ── Total Collection Summary sheet (mode-wise) ───────────────────────
    const summaryHeader = [
      "Payment Mode",
      "Admission Fee",
      "Development Charges",
      "Monthly Fee",
      "Transport Fee",
      "Total Amount",
    ];
    const summaryBody = Object.entries(modeWiseTotals).map(([mode, t]) => [
      mode,
      t.admission,
      t.development,
      t.monthly,
      t.transport,
      t.paidAmount,
    ]);
    const grand = Object.values(modeWiseTotals).reduce(
      (acc, t) => {
        acc.admission += t.admission || 0;
        acc.development += t.development || 0;
        acc.monthly += t.monthly || 0;
        acc.transport += t.transport || 0;
        acc.paidAmount += t.paidAmount || 0;
        return acc;
      },
      { admission: 0, development: 0, monthly: 0, transport: 0, paidAmount: 0 },
    );
    summaryBody.push([
      "Grand Total",
      grand.admission,
      grand.development,
      grand.monthly,
      grand.transport,
      grand.paidAmount,
    ]);

    const summarySheet = XLSX.utils.aoa_to_sheet([
      summaryHeader,
      ...summaryBody,
    ]);
    XLSX.utils.book_append_sheet(
      workbook,
      summarySheet,
      sanitizeSheetName("Total Collection Summary", usedSheetNames),
    );
  }

  // ── Admission Form Fee sheet ────────────────────────────────────────────
  let admissionFormModeWiseTotals = {};
  let admissionFormGrandTotal = 0;

  if (includeAdmissionForm) {
    const header = [
      "SL No.",
      "Form No.",
      "Apply Date",
      "Student Name",
      "Class",
      "Father's Name",
      "Contact No.",
      "Amount",
      "Mode",
      "Payment ID",
      "Bank",
      "Collected By",
    ];

    const body = (admissionFormData || []).map((item, i) => {
      const amount = Number(item.amount) || 0;
      admissionFormGrandTotal += amount;

      const mode = item.mode || "N/A";
      admissionFormModeWiseTotals[mode] =
        (admissionFormModeWiseTotals[mode] || 0) + amount;

      return [
        i + 1,
        item.formNo || "",
        item.applyDate || "",
        item?.student?.name || "",
        item?.student?.Class || "",
        item?.student?.fatherName || "",
        item?.student?.contactNo || "",
        amount,
        mode,
        item.paymentId || "-",
        item.bank || "-",
        item.collectedBy || "N/A",
      ];
    });

    const sheetData = body.length
      ? [
          header,
          ...body,
          [
            "Total",
            "",
            "",
            "",
            "",
            "",
            "",
            admissionFormGrandTotal,
            "",
            "",
            "",
            "",
          ],
        ]
      : [header, ["No Admission Form Fee Records"]];

    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    XLSX.utils.book_append_sheet(
      workbook,
      ws,
      sanitizeSheetName("Admission Form Fee", usedSheetNames),
    );
  }

  // ── Miscellaneous Fee sheet ──────────────────────────────────────────────
  let miscModeWiseTotals = {};
  let miscGrandTotal = 0;

  if (includeMiscFee) {
    const header = [
      "SL No.",
      "Receipt No.",
      "Form No.",
      "Student Name",
      "Type",
      "Amount",
      "Mode",
      "Payment ID",
      "Bank",
      "Collected By",
    ];

    const body = (miscellaneousFeesData || []).map((item, i) => {
      const amount = Number(item.amount) || 0;
      miscGrandTotal += amount;

      const mode = item.mode || "N/A";
      miscModeWiseTotals[mode] = (miscModeWiseTotals[mode] || 0) + amount;

      return [
        i + 1,
        item.receipt_no || "",
        item.formNo || "",
        item.studentName || "",
        item.type || "",
        amount,
        mode,
        item.paymentId || "-",
        item.bank || "-",
        item.collectedBy || "N/A",
      ];
    });

    const sheetData = body.length
      ? [header, ...body, ["Total", "", "", "", "", miscGrandTotal, "", "", "", ""]]
      : [header, ["No Miscellaneous Fee Records"]];

    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    XLSX.utils.book_append_sheet(
      workbook,
      ws,
      sanitizeSheetName("Misc Fee", usedSheetNames),
    );
  }

  // ── Final combined Summary sheet (mirrors the PDF's last page) ───────────
  if (includeAdmissionForm || includeMiscFee) {
    const allModes = Array.from(
      new Set([
        ...Object.keys(admissionFormModeWiseTotals),
        ...Object.keys(miscModeWiseTotals),
      ]),
    );

    const body = allModes.map((mode) => {
      const admFee = admissionFormModeWiseTotals[mode] || 0;
      const miscFee = miscModeWiseTotals[mode] || 0;
      return [mode, admFee, miscFee, admFee + miscFee];
    });

    body.push([
      "Grand Total",
      admissionFormGrandTotal,
      miscGrandTotal,
      admissionFormGrandTotal + miscGrandTotal,
    ]);

    const sheetData = [
      ["Payment Mode", "Admission Form Fee", "Miscellaneous Fee", "Total Amount"],
      ...body,
    ];
    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    XLSX.utils.book_append_sheet(
      workbook,
      ws,
      sanitizeSheetName("Summary", usedSheetNames),
    );
  }

  // ── Save ─────────────────────────────────────────────────────────────────
  const reportDateLabel = feeData[0]?.toDate
    ? `${new Date(feeData[0].date)
        .toLocaleDateString("en-GB")
        .split("/")
        .join("-")}_to_${new Date(feeData[0].toDate)
        .toLocaleDateString("en-GB")
        .split("/")
        .join("-")}`
    : new Date(feeData[0]?.date).toLocaleDateString("en-GB").split("/").join("-");

  const fileName = `DCR_Report_${reportDateLabel}.xlsx`;
  XLSX.writeFile(workbook, fileName);
};

export default DcrSchoolExcel;